import { faExpandAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ButtonGroup, IconButton } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';

export const ZOOM_LEVELS = [100, 200, 300, 400] as const;

const useStyles = makeStyles((theme: Theme) => createStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  zoomButton: {
    minWidth: theme.spacing(7),
    minHeight: theme.spacing(5.5),
  },
  enlarge: {
    minWidth: theme.spacing(5.5),
    minHeight: theme.spacing(5.5),
  },
  icon: {
    fontSize: theme.typography.body2.fontSize,
  },
}));

type SourceZoomControlsProps = {
  zoom: number;
  onZoom: (level: number) => void;
  onEnlarge?: () => void;
};

/** Zoom presets for the inline viewer plus the button that opens the enlarged page dialog. */
export const SourceZoomControls = ({ zoom, onZoom, onEnlarge }: SourceZoomControlsProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const zoomLabel: string = t('imports:viewer.zoom');
  const enlargeLabel: string = t('imports:viewer.enlarge');

  return (
    <div className={classes.root}>
      <ButtonGroup size="small" color="primary" disableElevation aria-label={zoomLabel}>
        {ZOOM_LEVELS.map((level) => (
          <Button
            key={level}
            aria-pressed={zoom === level}
            variant={zoom === level ? 'contained' : 'outlined'}
            onClick={() => onZoom(level)}
            className={classes.zoomButton}
          >
            {`${level}%`}
          </Button>
        ))}
      </ButtonGroup>
      {onEnlarge && (
        <IconButton
          aria-label={enlargeLabel}
          onClick={onEnlarge}
          className={classes.enlarge}
          size="large"
        >
          <FontAwesomeIcon icon={faExpandAlt} className={classes.icon} aria-hidden="true" />
        </IconButton>
      )}
    </div>
  );
};
